<template>
  <div id="app">
    <notifications
      group="all"
      classes="vue-notification main-notification"
      width="100%"
      position="bottom center"
    />
    <notifications
      group="toast"
      class="toast-noti"
      classes="vue-notification toast-notification"
      position="top right"
    />
    <div v-if="isLoading">
      <fingerprint-spinner class="spinner" :animation-duration="1500" :size="150" color="#136a8a"/>
    </div>

    <session-expired-modal v-if="showSessionExp"/>

    <router-view/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { FingerprintSpinner } from 'epic-spinners';
import { eventHub } from '@/utils/EventHub';
import SessionExpiredModal from '@/views/SessionExpiredModal.vue';

export default {
  name: 'app',

  metaInfo: function () {
    const title = this.brandingData.brand;
    const iconUrl = this.brandingData.brandIconUrl;
    return {
      title: title,
      link: [
        { rel: 'icon', href: iconUrl, sizes: '16x16', type: 'image/png' },
        { rel: 'icon', href: iconUrl, sizes: '32x32', type: 'image/png' },
        { rel: 'apple-touch-icon', href: iconUrl, type: 'image/png' }
      ],
    }
  },

  components: {
    FingerprintSpinner,
    SessionExpiredModal,
  },

  async created() {
    eventHub.$on('before-request', this.setLoading);
    eventHub.$on('request-error', this.unsetLoading);
    eventHub.$on('after-response', this.unsetLoading);
    eventHub.$on('response-error', this.unsetLoading);
    eventHub.$on('auth-error', this.handle401Err);
    eventHub.$on('server-error', this.handle5xxErr);
    eventHub.$on('too-many-requests', this.handle429Err);

    // Prevents initiating app if there is any error or site is disabled
    if(!this.isSiteEnabled || this.tooManyRequestErr) return;

    // Get initial content, also initiate app & session
    try {
      await this.$store.dispatch('siteDataStore/getInitialContent');
      await this.$store.dispatch('authStore/initiateAppSession');
      if (this.isSessionActive) {
        this.initiateApp();
      } else {
        this.$store.commit('shippingStore/resetAddresses');
        this.$store.commit('cartStore/resetOrders');
      }
    } catch (error) {
      console.log('App could not be initialized');
    }
  },

  data() {
    return {
      refCount: 0,
      isLoading: false,
    };
  },

  beforeDestroy() {
    eventHub.$off('before-request', this.setLoading);
    eventHub.$off('request-error', this.unsetLoading);
    eventHub.$off('after-response', this.unsetLoading);
    eventHub.$off('response-error', this.unsetLoading);
    eventHub.$off('auth-error', this.handle401Err);
    eventHub.$off('server-error', this.handle5xxErr);
    eventHub.$off('too-many-requests', this.handle429Err);
  },

  methods: {
    async initiateApp() {
      try {
        await this.$store.dispatch('cartStore/getCart');
        await this.$store.dispatch('shippingStore/addressAction', {
          address: null,
          action: 'get',
        });

        if (this.checkoutInitiated) {
          const reqObj = {
            address: this.selectedAddress,
            shippingMethod: this.shippingMethod,
          };

          await this.$store.dispatch('cartStore/createCheckout', reqObj);
        }
      } catch (error) {
        console.log(error);
      }
    },
    setLoading() {
      this.refCount += 1;
      this.isLoading = true;
    },

    unsetLoading() {
      if (this.refCount > 0) {
        this.refCount -= 1;
        this.isLoading = this.refCount > 0;
      }
    },

    handle401Err() {
      const actualPath = this.$route.path;
      if (actualPath !== '/login') {        
        // Delete all from local storage and deactivate animation
        this.$store.commit('shippingStore/resetAddresses');
        this.$store.commit('cartStore/resetOrders');
        this.$store.commit('authStore/logoutUser');
        this.unsetLoading();
        
        // Show a modal notification that session expired
        this.$store.commit('authStore/setShowSessionExp', true);
  
        /*
          Send you to login, and if login was succesful
          returns to the path you were before
        */
        this.$router.push({
          path: '/login',
          query: {
            previousPath: actualPath
          }
        });
      }
      else {
        this.unsetLoading();
      }
    },

    handle5xxErr() {
      this.unsetLoading();
      /*
        Uncomment the following lines if client:
        want to block user navigation at any time if server not responded
      */
      // this.$router.replace('/err-5xx');
      // this.$store.commit('backendStore/setBackendAvailable', false);
    },

    handle429Err() {
      this.unsetLoading();

      // By setting tooManyRequest in store, router will display specific error page until user is unbanned
      this.$store.commit('backendStore/setTooManyRequestsErr', true);

      this.$router.push({
        path: '/err-429'
      });
    },
  },


  computed: {
    ...mapGetters({
      isSiteEnabled: 'backendStore/isSiteEnabled',
      tooManyRequestErr: 'backendStore/tooManyRequestsErr',
      isSessionActive: 'authStore/isSessionActive',
      showSessionExp: 'authStore/showSessionExp',
      brandingData: 'siteDataStore/brandingData',
      shippingMethod: 'shippingStore/shippingMethod',
      selectedAddress: 'shippingStore/getSelectedAddress',
      checkoutInitiated: 'cartStore/checkoutInitiated',
    }),
  },
};
</script>

<style>
@import url('https://fonts.googleapis.com/css?family=Karla');

#app {
  font-family: 'Quicksand','Raleway', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  margin: 0px;
  color: rgba(34,34,34,.7);
  height: 100%;
  font-size: 0.93em;
}

@media (max-width: 768px) {
  #app {
    overflow-y: auto;
    overflow-x: hidden;
  }
}

html,
body {
  margin: 0px;
  height: 100%;
}

.container {
  margin: 0px;
}

.toast-notification {
  /* margin-top: 100px !important; */
  font-size: 0.8em !important;
  padding: 20px 10px !important;
}

.toast-noti {
  top: 100px !important;
}

.spinner {
  position: fixed !important;
  top: 0px !important;
  height: 100vh !important;
  width: 100% !important;
  z-index: 10000 !important;
  background: rgba(255, 255, 255, 0.8) !important;
}
</style>
